import { Theme, ThemeColor } from './theme';

const themePresets = {
  color: {
    black: '#1d1d1d',
    blackAlpha: 'rgba(0,0,0,0.7)',
    blackSuperAlpha: 'rgba(29, 29, 29, 0.1)',
    white: '#ffffff',
    pinkDark: '#e8038b',
    pink: '#ff0098',
    pinkLight: '#ff99d6',
    pinkPale: '#fae0ef',
    pinkSuperLight: '#ffebf7',
    blueMediumDark: '#004feb',
    blue: '#0055ff',
    blueMediumLight: '#99bbff',
    blueLight: '#d6e4ff',
    blueGreyDark: '#cad7ee',
    blueGrayMediumDark: '#6E8EC8',
    blueGrey: '#e0e9fa',
    blueSuperLight: '#ebf2ff',
    blueAlpha: 'rgba(214, 228, 255, 0.5)',
    greenSuperDark: '#007474',
    greenLightDark: '#009b9b',
    greenDark: '#00c9a5',
    greenMediumDark: '#DAEBEB',
    green: '#00dfb8',
    greenLight: '#d6faf3',
    greenSuperLight: '#E5F8F6',
    greenExtraLight: '#f6fafa',
    yellowExtraDark: '#FEC102',
    yellowDark: '#e6c800',
    yellow: '#fde110',
    yellowLight: '#FCF9E5',
    yellowExtraLight: '#ffecb3',
    greyDark: '#666666',
    greyMediumDark: '#4C4D50',
    grey: '#919191',
    greyMedium: '#cbcbcb',
    greyLight: '#ececec',
    greySuperLight: '#f5f5f5',
    greyLightBlue: '#f6f9ff',
    none: 'none',
    error: '#ff2a50',
    transparent: 'transparent',
    shadowLight: 'rgba(0, 0, 0, 0.1)',
  },
  spacer: {
    '0': '0',
    tiny: '2px',
    small: '4px',
    standard: '8px',
    medium: '10px',
    big: '16px',
    times: (times: number) => `${times * 8}px`,
    auto: 'auto',
    primary: '3',
  },
  dims: {
    tiny: '16px',
    small: '24px',
    standard: '32px',
    medium: '40px',
    big: '48px',
    ample: '56px',
    large: '64px',
    massive: '72px',
    huge: '88px',
  },
  font: {
    size: {
      minuscule: '10px',
      superTiny: '12px',
      tiny: '14px',
      small: '16px',
      standard: '18px',
      moderate: '20px',
      medium: '24px',
      big: '32px',
      huge: '40px',
    },
    family: {
      sans: 'Gilroy',
      serif: 'serif',
    },
    weight: {
      normal: 'normal',
      bold: 600,
    },
  },
  media: {
    xs: '540px',
    sm: '720px',
    md: '960px',
    lg: '1260px',
    max: '2000px',
  },
  border: {
    radius: {
      tiny: '4px',
      small: '8px',
      standard: '12px',
      big: '24px',
      large: '32px',
      primary: '0.5',
    },
  },
  layer: {
    main: 0,
    top: 1000,
    above: (layer: number): number => layer + 1,
    below: (layer: number): number => layer - 1,
  },
};

export const themeAbstraction = {
  keys: {
    primaryColor: <ThemeColor>'blue',
    secondaryColor: <ThemeColor>'pink',
    tertiaryColor: <ThemeColor>'pink',
    quaternaryColor: <ThemeColor>'blue',
    quinaryColor: <ThemeColor>'pinkSuperLight',
    sixfoldColor: <ThemeColor>'yellowDark',
    components: {
      collapsibleSectionPrimaryColors: <[ThemeColor, ThemeColor]>['green', 'greyLight'],
      businessTogglePrimaryColors: <[ThemeColor, ThemeColor]>['blue', 'pink'],
    },
  },
  themePadding: {
    cardPadding: [10, 5, 1],
  },
  themeColor: {
    primaryColor: themePresets.color.pink,
    secondaryLightColor: themePresets.color.pinkSuperLight,
    tertiaryColor: themePresets.color.blueLight,
    tertiaryLightColor: themePresets.color.blueLight,
    tertiarySuperDarkColor: themePresets.color.blue,
    quaternaryColor: themePresets.color.greyDark,
  },
  components: {
    teams: {
      primaryColor: themePresets.color.blueMediumLight,
      secondaryColor: themePresets.color.blue,
      tertiaryColor: themePresets.color.green,
      statusColors: ['blue', 'pink', 'pink'],
      statusBgColors: ['blueSuperLight', 'pinkSuperLight', 'pinkSuperLight'],
      navLink: {
        primaryColor: themePresets.color.greenDark,
        secondaryColor: themePresets.color.greyDark,
        tertiaryColor: themePresets.color.greenLight,
      },
    },
    licenses: {
      licensesStatusColors: ['greenDark', 'pink', 'yellowDark', 'yellowDark'],
      licensesBgColors: ['greenLight', 'pinkSuperLight', 'yellowLight', 'yellowLight'],
    },
    button: {
      primaryColor: themePresets.color.pink,
      secondaryColor: themePresets.color.pinkPale,
      tertiaryColor: themePresets.color.blue,
      quaternaryColor: themePresets.color.pink,
      quinaryColor: themePresets.color.pinkSuperLight,
      transparent: themePresets.color.transparent,
      primaryTextColor: themePresets.color.white,
      secondaryTextColor: themePresets.color.pink,
      primaryDisabledTextColor: themePresets.color.white,
      secondaryDisabledTextColor: themePresets.color.pinkLight,
      primaryDisabledColor: themePresets.color.pinkLight,
      secondaryDisabledColor: themePresets.color.pinkSuperLight,
      tertiaryDisabledColor: themePresets.color.blueMediumLight,
    },
    dropdown: {
      primaryColor: themePresets.color.pinkSuperLight,
    },
    checkbox: {
      primaryColor: themePresets.color.greenLight,
    },
    datePicker: {
      primaryBgColor: themePresets.color.pinkSuperLight,
    },
    statusChip: {
      statusChipColor: ['blue', 'greenDark', 'pink', 'yellowDark'],
      statusChipBgColor: [
        'blueSuperLight',
        'greenSuperLight',
        'pinkSuperLight',
        'yellowLight',
      ],
    },
    createDialog: {
      primaryBorderColor: themePresets.color.blueLight,
    },
    dropdownSelect: {
      primaryBgColor: themePresets.color.greySuperLight,
    },
    login: {
      backgroundPosition: 'top 4vh left 25%',
      logoWidth: '150px',
      logoHeight: '38px',
    },
  },

  backgroundColor: themePresets.color.greyLightBlue,
};

export const theme: Theme = { ...themeAbstraction, ...themePresets };
